import clsx from 'clsx'
import { APIError } from 'common/api/utils'
import {
  getCpmmLiquidityPoolWeights,
  getLiquidityProvidersNetTotal,
  getLiquidityWithdrawalShares,
  maximumRemovableLiquidity,
  removeCpmmLiquidity,
} from 'common/calculate-cpmm'
import { isMultiCpmm, type MarketContract } from 'common/contract'
import { isAdminId } from 'common/envs/constants'
import { LiquidityProvision } from 'common/liquidity-provision'
import { formatWithCommas } from 'common/util/format'
import { floatingEqual } from 'common/util/math'
import { sumBy } from 'lodash'
import { useState } from 'react'
import toast from 'react-hot-toast'
import { FeedLiquidity } from 'web/components/feed/feed-liquidity'
import { useLiquidity } from 'web/hooks/use-liquidity'
import { useUser } from 'web/hooks/use-user'
import { api } from 'web/lib/api/api'
import { track } from 'web/lib/service/analytics'
import { MoneyDisplay } from '../bet/money-display'
import { Button } from '../buttons/button'
import { MODAL_CLASS, Modal } from '../layout/modal'
import { Col } from '../layout/col'
import { Row } from '../layout/row'
import { BuyAmountInput } from '../widgets/amount-input'
import { InfoTooltip } from '../widgets/info-tooltip'
import { MOD_ACTION_TEXT_CLASS, ModActionBadge } from '../moderation/mod-action'

export function AddLiquidityModal(props: {
  contract: MarketContract
  isOpen: boolean
  setOpen: (open: boolean) => void
  // When set, subsidize a single answer (its own binary CPMM) instead of the whole market.
  answerId?: string
  answerText?: string
}) {
  const { contract, isOpen, setOpen, answerId, answerText } = props

  const liquidities = useLiquidity(contract.id)
  // Opened for an answer, its own contributors: those who subsidized it, and
  // whoever added it.
  const lps = (liquidities ?? []).filter(
    (lp) => !answerId || lp.answerId === answerId
  )

  const [amount, setAmount] = useState<number | undefined>(0)

  return (
    <Modal open={isOpen} setOpen={setOpen} size="sm" className={MODAL_CLASS}>
      <Col className="gap-6">
        {/* Header */}
        <div>
          <h2 className="text-primary-700 text-2xl font-semibold tracking-tight">
            Liquidity
          </h2>
          <p className="text-ink-500 mt-1 text-sm">
            {answerId
              ? `Subsidize "${
                  answerText ?? 'this answer'
                }" to incentivize more accurate predictions`
              : 'Subsidize this market to incentivize more accurate predictions'}
          </p>
        </div>

        {/* Stats Section */}
        <LiquidityStats contract={contract} answerId={answerId} />

        {/* Action Section */}
        <AddLiquidityControl
          contract={contract}
          amount={amount}
          setAmount={setAmount}
          answerId={answerId}
          liquidities={liquidities}
        />

        {/* Contributors Section */}
        {lps.length > 0 && (
          <div>
            <div className="text-ink-600 mb-2 text-xs font-medium uppercase tracking-wide">
              Contributors
            </div>
            <div className="bg-canvas-50 divide-ink-200 divide-y rounded-lg border">
              {lps.map((lp) => (
                <div key={lp.id} className="px-3 py-2.5">
                  <FeedLiquidity
                    liquidity={lp}
                    isCashContract={contract.token === 'CASH'}
                    avatarSize="xs"
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </Col>
    </Modal>
  )
}

function LiquidityStats(props: {
  contract: MarketContract
  answerId?: string
}) {
  const { contract, answerId } = props
  const isCashContract = contract.token === 'CASH'

  // Active liquidity is the total less subsidy still waiting to drizzle in:
  // the market's own and, on a multiple choice market, each open answer's.
  const openAnswers = isMultiCpmm(contract)
    ? contract.answers.filter((a) => !a.resolution)
    : []
  const pending =
    contract.mechanism === 'cpmm-1' || isMultiCpmm(contract)
      ? (contract.subsidyPool ?? 0) +
        sumBy(openAnswers, (a) => a.subsidyPool ?? 0)
      : 0
  const drizzled = contract.totalLiquidity - pending
  const answer = answerId
    ? openAnswers.find((a) => a.id === answerId)
    : undefined

  return (
    <Row className="bg-canvas-50 text-ink-600 flex-wrap gap-x-6 gap-y-2 rounded-lg border px-4 py-3 text-sm">
      <Row className="items-center gap-1.5">
        <span className="text-ink-500">
          {answerId ? 'Market liquidity:' : 'Liquidity:'}
        </span>
        <span className="text-ink-900 font-medium">
          <MoneyDisplay amount={drizzled} isCashContract={isCashContract} />
          {' / '}
          <MoneyDisplay
            amount={contract.totalLiquidity}
            isCashContract={isCashContract}
          />
        </span>
        <InfoTooltip
          text="Subsidies trickle in over time to prevent manipulation. Shows active / total liquidity."
          size="sm"
        />
      </Row>
      {!!answer?.subsidyPool && (
        <Row className="items-center gap-1.5">
          <span className="text-ink-500">Pending for this answer:</span>
          <span className="text-ink-900 font-medium">
            <MoneyDisplay
              amount={answer.subsidyPool}
              isCashContract={isCashContract}
            />
          </span>
          <InfoTooltip
            text="Subsidy for this answer trickles into its pool over time."
            size="sm"
          />
        </Row>
      )}
      {contract.mechanism === 'cpmm-1' && (
        <Row className="items-center gap-1.5">
          <span className="text-ink-500">Pool:</span>
          <span className="text-ink-900 font-medium">
            {formatWithCommas(Math.round(contract.pool.YES))} YES,{' '}
            {formatWithCommas(Math.round(contract.pool.NO))} NO shares
          </span>
        </Row>
      )}
    </Row>
  )
}

export function AddLiquidityControl(props: {
  contract: MarketContract
  amount: number | undefined
  setAmount: (amount: number | undefined) => void
  // When set, the subsidy targets a single answer (its own binary CPMM).
  answerId?: string
  // Every provision on the market, to split a withdrawal between providers.
  liquidities: LiquidityProvision[] | undefined
}) {
  const { contract, amount, setAmount, answerId, liquidities } = props
  const { id: contractId, slug, totalLiquidity } = contract

  const [error, setError] = useState<string | undefined>(undefined)
  const [isLoading, setIsLoading] = useState(false)

  const user = useUser()
  const contractOpenAndPublic =
    !contract.isResolved &&
    (contract.closeTime ?? Infinity) > Date.now() &&
    contract.visibility == 'public'

  const addLiquidityEnabled =
    user &&
    (contract.mechanism == 'cpmm-1' || isMultiCpmm(contract)) &&
    contractOpenAndPublic
  const [mode, setMode] = useState<'add' | 'remove'>('add')
  const canWithdraw =
    contract.mechanism === 'cpmm-1' &&
    !!user &&
    ((user.id === contract.creatorId && contract.token !== 'CASH') ||
      isAdminId(user.id)) &&
    // Only someone with a share of the pool can withdraw (remove-liquidity.ts)
    !!liquidities &&
    !!getCpmmLiquidityPoolWeights(liquidities)[user.id]
  // Can only withdraw because the user is an admin
  const withdrawIsAdminAction =
    canWithdraw &&
    !(user?.id === contract.creatorId && contract.token !== 'CASH')
  // A withdrawal goes back to every provider in proportion to their share,
  // and has to stay below what they've put in (getLiquidityWithdrawalShares).
  const maxWithdrawable =
    !canWithdraw || !liquidities
      ? 0
      : Math.min(
          contract.subsidyPool + maximumRemovableLiquidity(contract.pool),
          Math.ceil(getLiquidityProvidersNetTotal(liquidities)) - 1
        )
  const shares =
    canWithdraw && liquidities && mode === 'remove' && !!amount
      ? getLiquidityWithdrawalShares(liquidities, amount)
      : undefined
  const yourShare = shares && user ? shares[user.id] ?? 0 : 0
  const othersShare = shares && amount ? amount - yourShare : 0
  const otherProviders = shares
    ? Object.keys(shares).filter((id) => id !== user?.id).length
    : 0
  const aboveMax = mode === 'remove' && amount && amount > maxWithdrawable
  const newTotal = totalLiquidity + (amount ?? 0) * (mode === 'add' ? 1 : -1)

  const newTrickleQueue = !amount
    ? undefined
    : Math.max(0, contract.subsidyPool + amount * (mode === 'add' ? 1 : -1))

  const newPool =
    contract.mechanism === 'cpmm-1' && mode === 'remove' && !!amount
      ? amount > contract.subsidyPool &&
        removeCpmmLiquidity(
          contract.pool,
          contract.p,
          amount - contract.subsidyPool
        ).newPool
      : undefined

  const isCashContract = contract.token === 'CASH'

  const submit = async () => {
    if (!amount) return

    setIsLoading(true)

    try {
      if (mode === 'add') {
        await api('market/:contractId/add-liquidity', {
          amount,
          contractId,
          ...(answerId ? { answerId } : {}),
        })
        toast.success(
          <>
            Success! Added{' '}
            <MoneyDisplay amount={amount} isCashContract={isCashContract} />{' '}
            subsidy.
          </>
        )
      } else {
        const yours = await api('market/:contractId/remove-liquidity', {
          amount,
          contractId,
        })
        toast.success(
          <>
            Success! Withdrew{' '}
            <MoneyDisplay amount={amount} isCashContract={isCashContract} /> of
            liquidity, of which{' '}
            <MoneyDisplay
              amount={-yours.amount}
              isCashContract={isCashContract}
            />{' '}
            came back to you.
          </>
        )
      }

      setAmount(undefined)
      setError(undefined)
      if (mode === 'add') {
        track('add liquidity', { amount, contractId, slug })
      } else {
        track('remove liquidity', { amount, contractId, slug })
      }
    } catch (e) {
      if (e instanceof APIError) {
        setError(e.message)
      } else {
        setError('Server error')
      }
    } finally {
      setIsLoading(false)
    }
  }

  if (!addLiquidityEnabled) return null

  const hasChanges = !!amount && amount > 0

  return (
    <Col className="gap-4">
      {/* Mode Toggle */}
      {canWithdraw && (
        <div className="bg-canvas-50 inline-flex self-start rounded-lg p-1">
          <button
            onClick={() => setMode('add')}
            className={clsx(
              'rounded-md px-4 py-1.5 text-sm font-medium transition-all',
              mode === 'add'
                ? 'bg-canvas-0 text-ink-900 shadow-sm'
                : 'text-ink-500 hover:text-ink-700'
            )}
          >
            Add
          </button>
          <button
            onClick={() => {
              setMode('remove')
              if (!amount) setAmount(Math.floor(maxWithdrawable))
            }}
            className={clsx(
              'rounded-md px-4 py-1.5 text-sm font-medium transition-all',
              withdrawIsAdminAction && [
                'inline-flex items-center',
                MOD_ACTION_TEXT_CLASS,
              ],
              mode === 'remove'
                ? [
                    'bg-canvas-0 shadow-sm',
                    !withdrawIsAdminAction && 'text-ink-900',
                  ]
                : !withdrawIsAdminAction && 'text-ink-500 hover:text-ink-700'
            )}
          >
            {withdrawIsAdminAction && <ModActionBadge admin className="mr-1" />}
            Withdraw
          </button>
        </div>
      )}

      {mode === 'remove' && (
        <p className="text-ink-500 text-sm">
          Withdrawn liquidity goes back to everyone who added liquidity, in
          proportion to what each put in. They're notified, and can add theirs
          back.
        </p>
      )}

      {/* Amount Input */}
      <div>
        <BuyAmountInput
          amount={amount}
          onChange={setAmount}
          error={error}
          setError={setError}
          disabled={false}
          quickButtonAmountSize="large"
          token={isCashContract ? 'CASH' : 'M$'}
          disregardUserBalance={mode === 'remove'}
          maximumAmount={mode === 'remove' ? maxWithdrawable : undefined}
        />
      </div>

      {/* Preview Changes */}
      {hasChanges && contract.mechanism === 'cpmm-1' && (
        <div className="bg-canvas-50 rounded-lg border px-4 py-3">
          <div className="text-ink-600 mb-2 text-xs font-medium uppercase tracking-wide">
            Preview
          </div>
          <div className="space-y-1.5 text-sm">
            <PreviewRow
              label="Total liquidity"
              from={
                <MoneyDisplay
                  amount={totalLiquidity}
                  isCashContract={isCashContract}
                />
              }
              to={
                <MoneyDisplay
                  amount={newTotal}
                  isCashContract={isCashContract}
                />
              }
            />
            {newTrickleQueue != undefined &&
              !floatingEqual(newTrickleQueue, contract.subsidyPool) && (
                <PreviewRow
                  label="Pending subsidy"
                  from={
                    <MoneyDisplay
                      amount={contract.subsidyPool}
                      isCashContract={isCashContract}
                    />
                  }
                  to={
                    <MoneyDisplay
                      amount={newTrickleQueue}
                      isCashContract={isCashContract}
                    />
                  }
                />
              )}
            {shares && (
              <PreviewRow
                label="Back to you"
                to={
                  <MoneyDisplay
                    amount={yourShare}
                    isCashContract={isCashContract}
                  />
                }
              />
            )}
            {shares && otherProviders > 0 && (
              <PreviewRow
                label={`Back to ${otherProviders} other provider${
                  otherProviders === 1 ? '' : 's'
                }`}
                to={
                  <MoneyDisplay
                    amount={othersShare}
                    isCashContract={isCashContract}
                  />
                }
              />
            )}
            {newPool && (
              <PreviewRow
                label="Pool"
                from={
                  <>
                    {formatWithCommas(Math.round(contract.pool.YES))} /{' '}
                    {formatWithCommas(Math.round(contract.pool.NO))}
                  </>
                }
                to={
                  <span className={clsx(aboveMax && 'text-scarlet-500')}>
                    {formatWithCommas(Math.round(newPool.YES))} /{' '}
                    {formatWithCommas(Math.round(newPool.NO))}
                  </span>
                }
              />
            )}
          </div>
        </div>
      )}

      {/* Submit Button */}
      <Button
        onClick={submit}
        disabled={isLoading || !!error || !amount}
        loading={isLoading}
        color={mode === 'add' ? 'indigo' : 'yellow'}
        size="xl"
        className="w-full"
      >
        {mode === 'add' ? 'Add liquidity' : 'Withdraw liquidity'}
      </Button>
    </Col>
  )
}

function PreviewRow(props: {
  label: string
  from?: React.ReactNode
  to: React.ReactNode
}) {
  const { label, from, to } = props
  return (
    <Row className="items-center justify-between">
      <span className="text-ink-500">{label}</span>
      <Row className="items-center gap-2">
        {from !== undefined && (
          <>
            <span className="text-ink-400">{from}</span>
            <span className="text-ink-400">&rarr;</span>
          </>
        )}
        <span className="text-ink-900 font-medium">{to}</span>
      </Row>
    </Row>
  )
}
